/**
 * The vendor marks on the SSO buttons.
 *
 * Inlined rather than `<img src>` on purpose: an external asset costs a request and lands after
 * first paint, so the three buttons visibly reflow as their marks arrive. On a sign-in screen
 * that is the first thing a customer sees of the product.
 *
 * Traced from `../assets/logos/*.svg`, which are kept alongside as the record of what was
 * inlined and as the files to swap at launch. Each keeps its own native viewBox — Microsoft's is
 * 23×23, Google's 48×48, Okta's 24×24 — because rescaling the coordinates by hand is how a
 * traced mark quietly stops matching the original.
 *
 * Three rules apply to all of them, and they are the vendors' rules rather than ours. The mark is
 * never recoloured, never rotated, and never placed on a coloured fill, which is why `.sso-mark`
 * has no background and why every fill below is an explicit hex rather than `currentColor`.
 *
 * The source files carry `role="img"` and an `aria-label`. Both are dropped here in favour of
 * `aria-hidden`, because the button already names the vendor in text and a screen reader
 * announcing "Microsoft, Microsoft Entra ID" is noise rather than extra information.
 *
 * These are placeholders traced to the correct brand colours. Before launch each must be replaced
 * with the vendor's own asset and its sign-in button guidelines followed — Microsoft identity
 * platform branding, Google Identity branding, Okta brand assets. The Okta mark in particular is
 * a simplified ring standing in for their real lockup. Tracked in PLAN.md.
 */

export interface VendorMarkProps {
  /** Both dimensions. 20 inside the 26px button box, per the handoff. */
  size?: number
}

/**
 * The shared wrapper, so all three marks agree on size and accessibility without repeating it.
 */
function Mark({
  size = 20,
  viewBox,
  children,
}: VendorMarkProps & { viewBox: string; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      // The button text names the vendor, so the mark is decoration.
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

export function MicrosoftMark({ size }: VendorMarkProps) {
  return (
    <Mark size={size} viewBox="0 0 23 23">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </Mark>
  )
}

export function GoogleMark({ size }: VendorMarkProps) {
  return (
    <Mark size={size} viewBox="0 0 48 48">
      <path
        fill="#4285f4"
        d="M45.1 24.5c0-1.6-.1-3.1-.4-4.6H24v8.7h11.8c-.5 2.7-2.1 5-4.4 6.5v5.4h7.1c4.2-3.8 6.6-9.5 6.6-16z"
      />
      <path
        fill="#34a853"
        d="M24 46c6 0 11-2 14.6-5.4l-7.1-5.4c-2 1.3-4.5 2.1-7.5 2.1-5.8 0-10.7-3.9-12.4-9.1H4.3v5.6C7.9 41.1 15.4 46 24 46z"
      />
      <path
        fill="#fbbc05"
        d="M11.6 28.2c-.5-1.3-.7-2.7-.7-4.2s.3-2.9.7-4.2v-5.6H4.3C2.8 17.1 2 20.4 2 24s.8 6.9 2.3 9.8l7.3-5.6z"
      />
      <path
        fill="#ea4335"
        d="M24 10.8c3.3 0 6.2 1.1 8.5 3.3l6.3-6.3C35 4.2 30 2 24 2 15.4 2 7.9 6.9 4.3 14.2l7.3 5.6c1.7-5.2 6.6-9 12.4-9z"
      />
    </Mark>
  )
}

export function OktaMark({ size }: VendorMarkProps) {
  return (
    <Mark size={size} viewBox="0 0 24 24">
      <path
        fill="#00297a"
        d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 15a5 5 0 110-10 5 5 0 010 10z"
      />
    </Mark>
  )
}
